import React, {
  useState,
  FunctionComponent,
  useContext,
  useEffect,
} from "react";
import {
  styled,
  Block,
  Modal,
  PanelHeader,
  IModalProps,
  Icon,
  Button,
  ModalIconContainer,
  ModalTitleContainer,
  ModalSubTitle,
  ModalTitle,
  ModalDescription,
  Panel,
} from "../../../seenspire-library";
import workplaceFacebookLogo from "../../../assets/workplace-facebook-logo.png";
import PanelForm from "../../components/PanelForm/PanelForm";
import { Formik, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import { formatServerValidationErrors } from "../../util";
import InputField from "../../components/InputField/InputField";
import { AppContext } from "../../state";
import { useTranslation, Trans } from "react-i18next";
import {
  ModalButtons,
  FormModalButtons,
} from "../../components/ModalButtons/ModalButtons";
import {
  ISocialNetworksTypesConfig,
  SocialNetworkEnum,
} from "../../containers/AccountSocialNetworks/AccountSocialNetworks";
import { find } from "lodash";
import AsyncVideo from "../../containers/Dashboard/AsyncVideo";
import { TransWithStrong } from "../../components/TransText/TransText";
import { HELP_URL } from "../../backend/consts";

export type OAuthProcessStatus =
  | "INIT"
  | "IN_PROGRESS"
  | "CONNECTED"
  | "ERROR"
  | "ERROR_DUPLICATE";

const ModalList = styled.ul.attrs({})`
  padding-left: 18px;
  margin-top: 2px;
  margin-bottom: 0px;
`;

const ModalListItem = styled.li.attrs({})`
  color: #344563;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const ModalCalloutNote = styled(Block)`
  padding-top: 10px;
  color: #344563;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const SocialPanel = styled(Panel)`
  padding: 12px 16px;
`;

const SocialPanelHeader = styled(PanelHeader)`
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
`;

const CustomModalDescription = styled(ModalDescription)`
  padding-top: 0;
  margin-bottom: 57px;
`;

const StyledLink = styled.a`
  font-weight: 600;
`;

const ScaledIcon = styled(Icon)<{ scaleAmount: number }>`
  transform: ${({ scaleAmount }) => scaleAmount && `scale(${scaleAmount})`};
`;

const ManageSocialNetworkModal: FunctionComponent<
  IModalProps & {
    selectedNetwork: ISocialNetworksTypesConfig;
    reconnectConnectionId?: string;
  }
> = ({
  onSubmit,
  onCancel,
  selectedNetwork,
  reconnectConnectionId,
  customStyle,
  ...props
}) => {
  const { slug, client, account, theme } = useContext(AppContext);
  const [oAuthIframeScr, setOAuthIframeScr] = useState<string>();
  const [duplicateId, setDuplicateId] = useState<any>();
  const [isOAuth, setIsOAuth] = useState<boolean>();
  const [oAuthStatus, setOAuthStatus] = useState<OAuthProcessStatus>("INIT");
  const { t } = useTranslation();

  const [data, setData] = useState<any>();
  const [checkingOAuthStatusInterval, setCheckingOAuthStatusInterval] =
    useState<any>();

  useEffect(() => {
    // @TODO Clean up the none OAuth if backend isn't supporting it anymore
    setIsOAuth(
      true ||
        ![SocialNetworkEnum.workplace].includes(selectedNetwork.name as any)
    );
    setData({
      network: { id: selectedNetwork.id },
      token: "",
      client_id: "",
      client_secret: "",
    });
  }, [props.isOpen]);

  const validationSchema = Yup.object().shape({
    client_id: Yup.string().required("Please provide an App ID"),
    token: Yup.string().required("Please provide an Access Token"),
    client_secret: Yup.string().required("Please provide an App Secret"),
    network: Yup.object().shape({
      id: Yup.number().required(),
    }),
  });

  const submitFormData = async (values: any, actions: FormikHelpers<any>) => {
    try {
      const results = await client.NetworkConnections.postNetworkConnection(
        values
      );
      if (results.status === "duplicate") {
        setDuplicateId(results.id);
        setOAuthStatus("ERROR_DUPLICATE");
      } else {
        setOAuthStatus("CONNECTED");
      }
    } catch (error) {
      setOAuthStatus("ERROR");
    }
  };

  const submitData = async (values: any, actions: FormikHelpers<any>) => {
    if (oAuthStatus !== "INIT") return;
    if (!client) return;
    try {
      const { id } = !reconnectConnectionId
        ? // Create new network connection, and obtain id
          // @TODO USE SAME CALL TO ADD WORKPLACE APP INFO {token, client_id, client_secret}
          await client.NetworkConnections.postNetworkConnection({
            network: selectedNetwork.id,
          } as any)
        : // Reconnect to an existing network connection
          { id: reconnectConnectionId };

      const { url: oAuthLink } = (await client.NetworkConnections.startOauth(
        String(id)
      )) as any;
      window.open(
        oAuthLink,
        "Network connection",
        "scrollbars,status,resizable=1"
      );
      setOAuthStatus("IN_PROGRESS");
      const _checkingOAuthStatusInterval = setInterval(() => {
        client.NetworkConnections.getConnections().then((results: any) => {
          const currentProcess = find(
            results.data,
            (item) => item.id == id
          ) as any;

          switch (currentProcess && currentProcess.status) {
            case "duplicate":
              setDuplicateId(id);
              setOAuthStatus("ERROR_DUPLICATE");
              clearInterval(_checkingOAuthStatusInterval);
              return;
            case "expired":
              setOAuthStatus("IN_PROGRESS");
              return;
            case "connected":
              setOAuthStatus("CONNECTED");
              clearInterval(_checkingOAuthStatusInterval);
              return;
            case "refused":
              setOAuthStatus("ERROR");
              clearInterval(_checkingOAuthStatusInterval);
              return;
            default:
              setOAuthStatus("IN_PROGRESS");
          }
        });
      }, 3500);
      setCheckingOAuthStatusInterval(_checkingOAuthStatusInterval);
    } catch (error) {
      actions.setErrors(formatServerValidationErrors(error));
      actions.setSubmitting(false);
      clearInterval(checkingOAuthStatusInterval);
    }
  };

  const cleanUpDuplicateNetwork = async (id: any) => {
    await client.NetworkConnections.deleteNetworkConnection(id);
  };

  const closeModal = async () => {
    if (oAuthStatus === "ERROR_DUPLICATE") {
      await cleanUpDuplicateNetwork(duplicateId);
    }
    onSubmit();
  };

  const getNetworkDescription = (network: ISocialNetworksTypesConfig) => {
    console.log(network);
    switch (true) {
      case network.name === "instagrambusiness":
        return (
          <Trans i18nKey="accounts.connect_instagram_description">
            <span></span>
            <a href="https://www.facebook.com/business/help/897631030335607"></a>
            <span></span>
          </Trans>
        );

      case network.name === "workplace" && network.disabled:
        return (
          <Block>
            Extend the reach of employee-generated content to digital signage,
            making your company culture visible beyond mobile and web through
            Seenspire's integration with Workplace by Facebook.
            <br></br>
            <br></br>
            The integration is under review by Workplace and will be available
            soon.
          </Block>
        );

      case network.name === "yammer" && network.disabled:
        return (
          <Block>
            Extend the reach of employee-generated content to digital signage,
            making your company culture visible beyond mobile and web through
            Seenspire's integration with Microsoft Viva Engage.
            <br></br>
            <br></br>
            The integration is under review by Microsoft and will be available
            soon.
          </Block>
        );

      // case network.name === 'workplace':
      //   return t('accounts.connect_workplace_description');
      default:
        return selectedNetwork.name === "powerbi" ? t("accounts.connect_to_x_description_dashboard", {
          sourceName: selectedNetwork.title,
        }) : t("accounts.connect_to_x_description", {
          sourceName: selectedNetwork.title,
        });
    }
  };
  const errorContent = (
    <>
      <ModalIconContainer padding={{ top: 60 }}>
        <Icon iconSize="larger" name="notice" />
      </ModalIconContainer>
      <ModalTitleContainer>
        <ModalTitle>Error occurred</ModalTitle>
      </ModalTitleContainer>
      <ModalDescription>
        An error has occurred, please try again later.
      </ModalDescription>
    </>
  );

  const errorDuplicateContent = (
    <>
      <ModalIconContainer padding={{ top: 60 }}>
        <Icon iconSize="larger" name="error" />
      </ModalIconContainer>
      <ModalTitleContainer>
        <ModalTitle>{t("social.duplicate_account_title")}</ModalTitle>
      </ModalTitleContainer>
      <ModalDescription margin={{ bottom: 40 }}>
        <Trans i18nKey="social.duplicate_account_description">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <StyledLink href={HELP_URL} target="_blank">
            read this
          </StyledLink>
        </Trans>
      </ModalDescription>
    </>
  );

  const connectedContent = (
    <>
      <ModalIconContainer padding={{ top: 40 }}>
        <Icon iconSize="larger" name="confirmation" />
      </ModalIconContainer>
      <ModalTitleContainer margin={{ top: 36 }}>
        <ModalTitle>
          {t("social.network_connection_connected_title")}
        </ModalTitle>
      </ModalTitleContainer>
      <CustomModalDescription>
        {t("social.network_connection_connected_description", {
          networkName: selectedNetwork.title,
        })}
      </CustomModalDescription>
    </>
  );

  const socialNetworkIconContent = (
    <ModalIconContainer padding={{ top: 40 }}>
      <ScaledIcon
        scaleAmount={0.7}
        iconSize="larger"
        name={selectedNetwork.icon}
      />
    </ModalIconContainer>
  );

  const socialCalloutPanelDescription = selectedNetwork.listItems && (
    <Block>
      <ModalList>
        {selectedNetwork.listItems.map((item: string, i: number) => (
          <ModalListItem key={i}>{item}</ModalListItem>
        ))}
      </ModalList>
      {selectedNetwork.note && (
        <ModalCalloutNote>
          <TransWithStrong>
            <Trans i18nKey={selectedNetwork.note} />
          </TransWithStrong>
        </ModalCalloutNote>
      )}
    </Block>
  );

  const socialCalloutPanel = selectedNetwork.listItems && (
    <Block padding={{ top: 47, bottom: 14 }}>
      <SocialPanel>
        <Block color="gray" shade="darkest">
          {socialCalloutPanelDescription}
        </Block>
      </SocialPanel>
    </Block>
  );

  const noneOAuthIconContent = (
    <>
      <Block
        display="grid"
        padding={{ all: 20 }}
        justifyContent="center"
        gridGap={20}
      >
        <img src={workplaceFacebookLogo} />
      </Block>

      {!selectedNetwork.disabled && (
        <ModalTitleContainer>
          <ModalSubTitle>{t("Account Authentication")}</ModalSubTitle>
          <ModalTitle>{selectedNetwork.title}</ModalTitle>
        </ModalTitleContainer>
      )}
    </>
  );

  if (!account) return null;

  return (
    <Modal
      theme={theme}
      customStyle={{
        content: {
          display: "grid",
          minWidth: "unset",
          minHeight: "420px",
          gridGap: 10,
        },
        overlay: { ...(customStyle && customStyle.overlay) },
      }}
      {...props}
    >
      {isOAuth && !selectedNetwork.disabled && (
        <>
          {oAuthStatus === "ERROR_DUPLICATE" && errorDuplicateContent}

          {oAuthStatus === "ERROR" && errorContent}

          {oAuthStatus === "CONNECTED" && connectedContent}

          {oAuthStatus === "IN_PROGRESS" && (
            <>
              {socialNetworkIconContent}
              <Block>
                <ModalTitleContainer>
                  <ModalTitle margin={{ top: 26 }}>
                    {t("social.network_connection_connecting")}
                  </ModalTitle>
                </ModalTitleContainer>
                <ModalDescription margin={{ bottom: 54 }}>
                  {t("social.network_connection_connecting_title")}
                </ModalDescription>
              </Block>
            </>
          )}

          {oAuthStatus === "INIT" && (
            <>
              {socialNetworkIconContent}
              <Block>
                <ModalTitleContainer>
                  <ModalSubTitle> </ModalSubTitle>
                  <ModalTitle>
                    {t("accounts.connect_to_x_network", {
                      name: selectedNetwork.title,
                    })}
                  </ModalTitle>
                </ModalTitleContainer>
                <ModalDescription>
                  {getNetworkDescription(selectedNetwork)}
                </ModalDescription>
                {socialCalloutPanel}
              </Block>
            </>
          )}

          <Formik initialValues={{}} onSubmit={submitData} enableReinitialize>
            {(form) => (
              <>
                {(oAuthStatus === "INIT" || oAuthStatus === "IN_PROGRESS") && (
                  <FormModalButtons
                    ctaNativeColor={selectedNetwork.nativeColor}
                    outsideForm
                    form={form}
                    onCancel={() => {
                      clearInterval(checkingOAuthStatusInterval);
                      onCancel();
                    }}
                    submit={t(
                      `${
                        reconnectConnectionId
                          ? `Reconnect to ${selectedNetwork.title}`
                          : t("accounts.connect_to_x_network_button", {
                              name: selectedNetwork.title,
                            })
                      }`
                    )}
                    submitting={t(`Connecting to ${selectedNetwork.title} ...`)}
                    cancel={t("Close")}
                  />
                )}

                {oAuthStatus === "CONNECTED" && (
                  <Button
                    elementWidth="block"
                    onClick={() => onSubmit()}
                    buttonType="success"
                  >
                    {t("social.network_connection_connected_button")}
                  </Button>
                )}
                {(oAuthStatus === "ERROR" ||
                  oAuthStatus === "ERROR_DUPLICATE") && (
                  <Button
                    elementWidth="block"
                    onClick={() => closeModal()}
                    buttonType="outline"
                  >
                    {t("Close")}
                  </Button>
                )}
              </>
            )}
          </Formik>
        </>
      )}

      {!isOAuth && !selectedNetwork.disabled && (
        <Block style={{ maxWidth: 620, width: "100%" }} justifySelf="center">
          {oAuthStatus === "ERROR_DUPLICATE" && errorDuplicateContent}

          {oAuthStatus === "ERROR" && errorContent}

          {oAuthStatus === "CONNECTED" && connectedContent}

          {oAuthStatus === "INIT" && (
            <>
              {noneOAuthIconContent}

              <ModalDescription>
                {getNetworkDescription(selectedNetwork)}
              </ModalDescription>

              <Block
                position="relative"
                padding={{ top: 50, bottom: 25 }}
                display="grid"
                justifyContent="center"
                justifyItems="center"
                gridGap={5}
              >
                <AsyncVideo
                  withContainer={false}
                  src={
                    "https://static.seenspire.com/assets/portals/img/fw_auth.mp4"
                  }
                  style={{
                    height: "300px",
                    maxWidth: "530px",
                    position: "unset",
                  }}
                />
              </Block>

              <Block display="grid" gridGap={20}>
                <ModalButtons
                  onCancel={() => {
                    onCancel();
                  }}
                  submit={
                    <Block
                      display="grid"
                      direction="column"
                      justifyContent="center"
                    >
                      <Block>Next</Block>
                      <Icon name="longArrowRight" />
                    </Block>
                  }
                  onSubmit={() => {
                    setOAuthStatus("IN_PROGRESS");
                  }}
                  cancel={t("Close")}
                />
              </Block>
            </>
          )}

          {oAuthStatus === "IN_PROGRESS" && (
            <>
              {noneOAuthIconContent}
              <Block display="grid" padding={{ top: 50 }} gridGap={20}>
                <Formik
                  initialValues={data}
                  onSubmit={submitFormData}
                  validationSchema={validationSchema}
                  enableReinitialize
                >
                  {(form) => (
                    <>
                      <PanelForm
                        form={form}
                        hideHeader
                        innerContainerProps={{ gridTemplate: {} }}
                      >
                        <Field
                          name="client_id"
                          type="text"
                          title={t("App ID")}
                          component={InputField}
                        />
                        <Field
                          name="client_secret"
                          type="text"
                          title={t("App Secret")}
                          component={InputField}
                        />
                        <Field
                          name="token"
                          type="text"
                          title={t("Access Token")}
                          component={InputField}
                        />
                      </PanelForm>

                      <FormModalButtons
                        outsideForm
                        form={form}
                        onCancel={() => {
                          setOAuthStatus("INIT");
                        }}
                        submit={t("Connect Workplace")}
                        submitting={t(
                          `Connecting to ${selectedNetwork.title} ...`
                        )}
                        cancel={t("Back")}
                      />
                    </>
                  )}
                </Formik>
              </Block>
            </>
          )}

          {oAuthStatus === "CONNECTED" && (
            <Button
              elementWidth="block"
              onClick={() => onSubmit()}
              buttonType="success"
            >
              {t("social.network_connection_connected_button")}
            </Button>
          )}
          {(oAuthStatus === "ERROR" || oAuthStatus === "ERROR_DUPLICATE") && (
            <Button
              elementWidth="block"
              onClick={() => closeModal()}
              buttonType="outline"
            >
              {t("Close")}
            </Button>
          )}
        </Block>
      )}

      {selectedNetwork.disabled && (
        <Block style={{ maxWidth: 620, width: "100%" }}>
          {isOAuth ? socialNetworkIconContent : noneOAuthIconContent}

          <Block>
            <ModalTitleContainer>
              <ModalSubTitle> </ModalSubTitle>
              <ModalTitle>{`${selectedNetwork.title} coming soon!`}</ModalTitle>
            </ModalTitleContainer>
            <ModalDescription>
              <Block padding={{ bottom: 30 }}>
                {getNetworkDescription(selectedNetwork)}
              </Block>
            </ModalDescription>
          </Block>

          {/* @TODO Add video once ready by product team */}
          {/* <Block position="relative" padding={{ top: 50, bottom: 25 }} display="grid" justifyContent="center" justifyItems="center" gridGap={5}>
            <AsyncVideo withContainer={false} src={'https://static.seenspire.com/assets/portals/img/fw_auth.mp4'}
              style={{ height: '300px', maxWidth: '530px', position: 'unset' }} />
          </Block> */}

          <Button
            margin={{ top: 10 }}
            elementWidth="block"
            onClick={() => onCancel()}
            buttonType="cta"
          >
            Got it
          </Button>
        </Block>
      )}
    </Modal>
  );
};

export { ManageSocialNetworkModal };
